import { createContext, useContext, useEffect } from 'react';
import type { PropsWithChildren } from 'react';

import { useTheme as useThemeHook } from '@/shared/hooks/useTheme';
import type { ThemeMode } from '@/shared/hooks/useTheme';

type ThemeContextType = {
  theme: ThemeMode;
  setTheme: (theme: ThemeMode) => void;
  toggleTheme: () => void;
  isDark: boolean;
  isLight: boolean;
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

/**
 * 애플리케이션 전역 테마 Provider
 * - Light/Dark 테마 전환
 * - localStorage 기반 테마 저장
 * - 시스템 테마 기본값 사용
 */
export const ThemeProvider = ({ children }: PropsWithChildren) => {
  const themeHook = useThemeHook();

  // 초기 마운트 시 테마 적용
  useEffect(() => {
    document.documentElement.setAttribute('data-opds-theme', themeHook.theme);
  }, [themeHook.theme]);

  return <ThemeContext.Provider value={themeHook}>{children}</ThemeContext.Provider>;
};

/**
 * 테마 컨텍스트를 사용하는 훅
 * @throws {Error} ThemeProvider 외부에서 사용 시
 */
export const useTheme = () => {
  const context = useContext(ThemeContext);

  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }

  return context;
};
